'use client';

import { apiFetch } from '@/lib/utils/api';
import { Stack, Text } from '@slims/ui/atoms';
import {
	AdminTable,
	type AdminTableColumn,
	AdminTableSkeleton,
	IconLink,
	Pill,
	type PillProps,
} from '@slims/ui/molecules';
import Link from '@/lib/next/link';
import { useEffect, useState } from 'react';

interface User {
	id: string;
	email: string;
	fullName: string | null;
	role: string;
	/** Per-CER memberships (org + role). */
	orgs?: { id: string; nome: string; role: string }[];
	createdAt: string;
}

const roleLabel: Record<string, string> = {
	superadmin: 'Super Admin',
	admin: 'Amministratore',
	referent: 'Referente',
	member: 'Socio',
};

const roleTone: Record<string, PillProps['tone']> = {
	superadmin: 'warn',
	admin: 'accent',
	referent: 'purple',
	member: 'muted',
};

const columns: AdminTableColumn<User>[] = [
	{ key: 'email', header: 'Email', variant: 'mono', render: (u) => u.email },
	{ key: 'nome', header: 'Nome', variant: 'muted', render: (u) => u.fullName ?? '-' },
	{
		key: 'ruolo',
		header: 'Ruolo',
		render: (u) => <Pill tone={roleTone[u.role] ?? 'muted'}>{roleLabel[u.role] ?? u.role}</Pill>,
	},
	{
		key: 'org',
		header: 'Organizzazioni',
		variant: 'muted',
		render: (u) => (u.orgs && u.orgs.length > 0 ? u.orgs.map((o) => o.nome).join(', ') : '-'),
	},
	{
		key: 'azioni',
		header: '',
		align: 'right',
		render: (u) => (
			<IconLink href={`/users/${u.id}`} linkComponent={Link}>
				Modifica
			</IconLink>
		),
	},
];

export default function UsersPage() {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		apiFetch('/api/admin/users')
			.then((r) => r.json())
			.then((data: unknown) => {
				setUsers(Array.isArray(data) ? (data as User[]) : []);
				setLoading(false);
			})
			.catch(() => setLoading(false));
	}, []);

	return (
		<Stack gap="xl">
			<Text as="h1" variant="display">
				Utenti
			</Text>

			{loading ? (
				<AdminTableSkeleton columns={columns} rows={6} />
			) : (
				<AdminTable
					columns={columns}
					rows={users}
					rowKey={(u) => u.id}
				/>
			)}
		</Stack>
	);
}
